import React, { useEffect, useMemo } from 'react';
import {
  StatsigProvider,
  useClientAsyncInit,
  type StatsigUser,
} from '@statsig/react-bindings';
import { StatsigAutoCapturePlugin } from '@statsig/web-analytics';
import { StatsigSessionReplayPlugin } from '@statsig/session-replay';
import { getTokenClaims, type TokenClaims } from '@center-technologies/shared-api-clients';
import { registerAnalyticsClient } from './analytics';
import { resolveStatsigUser } from './userIdentity';
import { getOrCreateSessionAnonymousId } from './anonymousId';

/**
 * Supplied by the consuming app to tell the analytics provider what the
 * active subject / org are. Keeps the shared package decoupled from each
 * app's state-management story (Redux, context, etc.).
 *
 * Implementations should be hooks: they will be called from inside the
 * provider's render and respect React's hook rules.
 */
export interface ActiveSubjectAdapter {
  /** The active subject id, or null when none. */
  useActiveSubjectId?: () => string | null;
  /** The active org id, or null when none. */
  useActiveOrgId?: () => string | null;
  /** True when the active subject is not the identity's primary subject. */
  useIsSubjectSwitched?: () => boolean;
}

interface StatsigClientProviderProps {
  children: React.ReactNode;
  /**
   * Optional adapter for surfacing active subject / org from the host app.
   * Apps without subjects (e.g., center-auth pre-login) can omit this.
   */
  activeSubjectAdapter?: ActiveSubjectAdapter;
  /**
   * Supplies claims for a host app that doesn't authenticate via Descope. Forwarded
   * to `getTokenClaims`, which defaults to decoding Descope's session token
   * (Center-PWA, EAINT-954 Phase 4); a host with another auth stack passes its own claims.
   */
  getFallbackToken?: () => TokenClaims | null;
}

const noopHook = () => null;
const noopBoolHook = () => false;

/**
 * Per-request ceiling for every Statsig network call. Ops-tunable via
 * `REACT_APP_STATSIG_NETWORK_TIMEOUT_MS` for the rare environment that needs
 * more; the default is short on purpose (see the note at the init call).
 */
const STATSIG_NETWORK_TIMEOUT_MS = Number(
  process.env['REACT_APP_STATSIG_NETWORK_TIMEOUT_MS'] ?? 3000
);

export const StatsigClientProvider = ({
  children,
  activeSubjectAdapter,
  getFallbackToken,
}: StatsigClientProviderProps) => {
  const clientKey = process.env['REACT_APP_STATSIG_CLIENT_KEY'];

  if (!clientKey) {
    if (process.env['NODE_ENV'] !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(
        '[Statsig] REACT_APP_STATSIG_CLIENT_KEY not set — analytics disabled. ' +
          'If this is unexpected: check environments/.env.local and restart `nx serve`.'
      );
    }
    return children;
  }

  return (
    <StatsigInitializer
      clientKey={clientKey}
      adapter={activeSubjectAdapter ?? {}}
      getFallbackToken={getFallbackToken}
    >
      {children}
    </StatsigInitializer>
  );
};

interface StatsigInitializerProps {
  clientKey: string;
  adapter: ActiveSubjectAdapter;
  children: React.ReactNode;
  getFallbackToken?: () => TokenClaims | null;
}

const StatsigInitializer = ({
  clientKey,
  adapter,
  children,
  getFallbackToken,
}: StatsigInitializerProps) => {
  const tokenClaims = getTokenClaims(getFallbackToken);

  // Adapter hooks are called unconditionally (hook rules). Apps that don't
  // provide one get a noop. Calling these inside the initializer (not the
  // outer provider) ensures they only run when Statsig is enabled.
  const useActiveSubjectId = adapter.useActiveSubjectId ?? noopHook;
  const useActiveOrgId = adapter.useActiveOrgId ?? noopHook;
  const useIsSubjectSwitched = adapter.useIsSubjectSwitched ?? noopBoolHook;

  const activeSubjectId = useActiveSubjectId();
  const activeOrgId = useActiveOrgId();
  const isSubjectSwitched = useIsSubjectSwitched();

  const enableReplay = process.env['REACT_APP_STATSIG_ENABLE_REPLAY'] === 'true';
  const anonymousId = useMemo(() => getOrCreateSessionAnonymousId(), []);

  const plugins = useMemo(() => {
    const list: Array<StatsigAutoCapturePlugin | StatsigSessionReplayPlugin> = [
      new StatsigAutoCapturePlugin(),
    ];
    if (enableReplay) {
      list.push(new StatsigSessionReplayPlugin());
    }
    return list;
  }, [enableReplay]);

  // Initial user is captured once at SDK init; subsequent changes flow
  // through updateUserAsync below.
  const initialUser = useMemo(
    () =>
      resolveStatsigUser({
        tokenClaims,
        anonymousId,
        activeSubjectId,
        activeOrgId,
        isSubjectSwitched,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Cast to StatsigUser at the SDK boundary. Our internal CenterStatsigUser
  // is a structurally-compatible subset; the SDK's stricter primitive type
  // accepts string/number/boolean which is what we feed it.
  /**
   * Statsig reaches three hosts on load — `featureassets.org/v1/initialize`,
   * `prodregistryv2.org/v1/rgstr` (event logging) and, when those do not
   * resolve, `cloudflare-dns.com/dns-query` (its DNS-over-HTTPS fallback for
   * discovering alternate endpoints). With no timeout configured the SDK
   * retried against unreachable hosts indefinitely.
   *
   * First paint never blocked on this — `loadingComponent` below renders
   * `children` — but the requests stayed pending, which means the page's
   * `load`/`networkidle` never settles. That silently taxed every Playwright
   * capture and e2e wait in this repo: `waitUntil: 'networkidle'` cannot fire
   * while an analytics beacon is still retrying.
   *
   * `networkTimeoutMs` bounds each attempt. It is deliberately short: analytics
   * is never worth delaying a page for, and a dropped event costs far less than
   * a hung wait.
   */
  const { client } = useClientAsyncInit(
    clientKey,
    initialUser as unknown as StatsigUser,
    { plugins, networkConfig: { networkTimeoutMs: STATSIG_NETWORK_TIMEOUT_MS } }
  );

  // Track context changes (login/logout, subject switch, org switch) and
  // push a new user to Statsig when they happen.
  useEffect(() => {
    if (!client) return;
    const nextUser = resolveStatsigUser({
      tokenClaims,
      anonymousId,
      activeSubjectId,
      activeOrgId,
      isSubjectSwitched,
    });
    if (
      nextUser.userID !== initialUser.userID ||
      JSON.stringify(nextUser.customIDs) !== JSON.stringify(initialUser.customIDs)
    ) {
      void client.updateUserAsync(nextUser as unknown as StatsigUser);
    }
  }, [
    client,
    tokenClaims,
    anonymousId,
    activeSubjectId,
    activeOrgId,
    isSubjectSwitched,
    initialUser,
  ]);

  // Register the client globally so non-React code (axios interceptors,
  // Redux middleware) can log events through the same SDK instance.
  useEffect(() => {
    registerAnalyticsClient(client ?? null);
    return () => {
      registerAnalyticsClient(null);
    };
  }, [client]);

  // loadingComponent is shown while the SDK initializes. Pass children so
  // the app renders immediately rather than blocking on third-party SDK init.
  // eslint-disable-next-line react/jsx-no-useless-fragment
  const renderChildren = <>{children}</>;

  return (
    <StatsigProvider client={client} loadingComponent={renderChildren}>
      {children}
    </StatsigProvider>
  );
};
